import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  afterNextRender,
  inject,
} from '@angular/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltip, MatTooltipDefaultOptions } from '@angular/material/tooltip';

/** Keeps tree truncation tooltips visible without letting their overlay intercept pointer completion. */
function nonInteractiveTooltipOptions(): MatTooltipDefaultOptions {
  const inherited = inject(MAT_TOOLTIP_DEFAULT_OPTIONS, { optional: true, skipSelf: true });
  return {
    showDelay: 0,
    hideDelay: 0,
    touchendHideDelay: 1500,
    ...inherited,
    disableTooltipInteractivity: true,
  };
}

/** Shows a Material tooltip only when the designated text node is horizontally truncated. */
@Directive({
  selector: '[appTruncatedTooltip]',
  standalone: true,
  hostDirectives: [MatTooltip],
  providers: [{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useFactory: nonInteractiveTooltipOptions }],
})
export class TruncatedTooltipDirective implements AfterViewInit, OnChanges, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly tooltip = inject(MatTooltip);
  private readonly injector = inject(Injector);
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private focusOrigin?: HTMLElement;
  private initialized = false;

  @Input() appTruncatedTooltip = '';
  @Input() appTruncatedTooltipTarget = '';
  @Input() appTruncatedTooltipFocusOrigin = '';

  constructor() {
    this.tooltip.disabled = true;
  }

  ngAfterViewInit(): void {
    this.initialized = true;
    this.observeTarget();
    this.observeFocusOrigin();
    // Measure once this render is done, not inside it. By now the tooltip's host binding (its
    // `mat-mdc-tooltip-disabled` class) has been checked, so enabling the tooltip here changed a
    // checked value: development builds then log ExpressionChangedAfterItHasBeenCheckedError
    // (NG0100) for every truncated tree label that appears (Issue #2956).
    afterNextRender(() => this.refresh(), { injector: this.injector });
  }

  ngOnChanges(): void {
    if (this.initialized) {
      this.observeTarget();
      this.observeFocusOrigin();
    }
    this.refresh();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.focusOrigin?.removeEventListener('focusin', this.handleFocusIn);
    this.focusOrigin?.removeEventListener('focusout', this.handleFocusOut);
  }

  @HostListener('mouseenter')
  @HostListener('focusin')
  refresh(): void {
    const target = this.target;
    this.tooltip.message = this.appTruncatedTooltip;
    this.tooltip.disabled = !target || !this.appTruncatedTooltip || target.scrollWidth <= target.clientWidth + 1;
  }

  private get target(): HTMLElement | null {
    if (!this.appTruncatedTooltipTarget) return this.host.nativeElement;
    return this.host.nativeElement.querySelector<HTMLElement>(this.appTruncatedTooltipTarget);
  }

  private observeTarget(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    const target = this.target;
    if (!target) return;

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.refresh());
      this.resizeObserver.observe(target);
    }
    if (typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver(() => this.refresh());
      this.mutationObserver.observe(target, { characterData: true, childList: true, subtree: true });
    }
  }

  private observeFocusOrigin(): void {
    this.focusOrigin?.removeEventListener('focusin', this.handleFocusIn);
    this.focusOrigin?.removeEventListener('focusout', this.handleFocusOut);
    this.focusOrigin = this.appTruncatedTooltipFocusOrigin
      ? (this.host.nativeElement.closest<HTMLElement>(this.appTruncatedTooltipFocusOrigin) ?? undefined)
      : undefined;
    this.focusOrigin?.addEventListener('focusin', this.handleFocusIn);
    this.focusOrigin?.addEventListener('focusout', this.handleFocusOut);
  }

  private readonly handleFocusIn = (): void => {
    this.refresh();
    if (!this.tooltip.disabled) this.tooltip.show();
  };

  private readonly handleFocusOut = (): void => this.tooltip.hide();
}
